import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { imprimirEtiquetasQr, baixarPdfEtiquetasQr } from '../lib/imprimirEtiquetasQr';

type Props = {
  code: string;
  title: string;
  subtitle?: string;
  value: string;
  onClose: () => void;
};

export default function QRPrintModal({ title, subtitle, value, onClose }: Props) {
  const [size, setSize] = useState<'sm' | 'md' | 'lg'>('md');
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [erroPdf, setErroPdf] = useState('');
  const qrWrapRef = useRef<HTMLDivElement | null>(null);
  const sizeOptions: Array<{ id: 'sm' | 'md' | 'lg'; label: string; desc: string; qr: number }> = [
    { id: 'sm', label: 'Pequeno', desc: '50 x 50 mm', qr: 132 },
    { id: 'md', label: 'Médio', desc: '72 x 95 mm', qr: 188 },
    { id: 'lg', label: 'Grande', desc: '90 x 120 mm', qr: 244 },
  ];
  const selected = sizeOptions.find((item) => item.id === size) || sizeOptions[1];

  function etiqueta() {
    const canvas = qrWrapRef.current?.querySelector('canvas') || null;
    if (!canvas) return null;
    return { titulo: title, subtitulo: subtitle, dataUrl: canvas.toDataURL('image/png') };
  }

  function imprimir() {
    const item = etiqueta();
    if (item) imprimirEtiquetasQr([item], size);
  }

  async function baixarPdf() {
    const item = etiqueta();
    if (!item) return;
    setErroPdf('');
    setGerandoPdf(true);
    try {
      await baixarPdfEtiquetasQr([item], size);
    } catch (e) {
      setErroPdf(e instanceof Error ? e.message : 'Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setGerandoPdf(false);
    }
  }

  return createPortal(
    <div className="modal-bg qr-screen-bg" onClick={onClose}>
      <div className="modal qr-screen-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qr-screen-shell">
          <div className="qr-screen-side noprint">
            <div className="qr-label-kicker">Etiqueta para impressão</div>
            <h3>{title}</h3>
            {subtitle && <p>{subtitle}</p>}
            <div className="qr-size-block">
              <div className="qr-size-title">Tamanho do QR Code</div>
              <div className="qr-size-list">
                {sizeOptions.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`qr-size-option${size === item.id ? ' on' : ''}`}
                    onClick={() => setSize(item.id)}
                  >
                    <strong>{item.label}</strong>
                    <span>{item.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            {erroPdf && <div className="msg-erro">{erroPdf}</div>}
            <div className="qr-screen-actions">
              <button className="btn sec" onClick={onClose}>Cancelar</button>
              <button className="btn sec" disabled={gerandoPdf} onClick={baixarPdf}>
                {gerandoPdf ? 'Gerando PDF...' : 'Baixar PDF'}
              </button>
              <button className="btn" onClick={imprimir}>Imprimir etiqueta</button>
            </div>
          </div>

          <div className="qr-screen-stage">
            <div className={`qr-sticker size-${size}`}>
              <div className="qr-sticker-title">{title}</div>
              {subtitle && <div className="qr-sticker-subtitle">{subtitle}</div>}
              <div className="qr-sticker-qr" ref={qrWrapRef}>
                <QRCodeCanvas value={value} size={selected.qr} includeMargin />
              </div>
              <div className="qr-sticker-brand subtle">Manux Service</div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
